import get from "lodash/get";
import React from "react";
import { ResponsiveHeroImage } from "@quintype/components";
import { loadRelatedStories } from "./related-stories-helper";
import { ImageFallback } from "../basic/image-fallback.js";
import { altTagOfHeroImages, isExternalStory } from "../../utils";
import PropTypes from "prop-types";
import "./related-stories.module.css";

function getImageFallback(props) {
  if (!props.showImage) return null;
  return props.showImage && props.story["hero-image-s3-key"] ? (
    <figure styleName="image">
      <ResponsiveHeroImage
        story={props.story}
        aspectRatio={[16, 9]}
        alt={altTagOfHeroImages(props.story)}
        defaultWidth={480}
        widths={[250, 480, 640]}
        sizes="(max-width: 500px) 98%, (max-width: 768px) 48%, 98%"
        imgParams={{ auto: ["format", "compress"], fit: "max" }}
      />
    </figure>
  ) : (
    <ImageFallback styleName="image image-cover" story={props.story} />
  );
}
getImageFallback.propTypes = {
  story: PropTypes.shape({
    story: PropTypes.object,
    "hero-image-s3-key": PropTypes.string,
    slug: PropTypes.string,
    headline: PropTypes.string,
    "author-name": PropTypes.string
  }),
  showImage: PropTypes.bool
};

function RelatedStoryCard(props) {
  const headlineStyles = props.showImage ? "title-bold" : "title";
  const externalLink = isExternalStory(props.story);
  // The `props.story` can be an item from items of a collection or a story itself.
  // assigning it accordinlgy.
  return !props.story ? null : (
    <div styleName="card">
      <a href={externalLink || `${props.story.url}`} target={externalLink ? "_blank" : "_self"}>
        {getImageFallback(props)}
        <h2 styleName={headlineStyles}>
          <bdi dangerouslySetInnerHTML={{ __html: props.story.headline }} />
        </h2>
        <div styleName="author">{get(props.story, ["authors", 0, "name"], props.story["author-name"])}</div>
      </a>
    </div>
  );
}
RelatedStoryCard.propTypes = {
  story: PropTypes.shape({
    story: PropTypes.object,
    slug: PropTypes.string,
    headline: PropTypes.string,
    "author-name": PropTypes.string
  }),
  showImage: PropTypes.bool
};
class RelatedStories extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      relatedStories: this.props.staticRelatedStories || []
    };
  }

  updateRelatedStories(story) {
    loadRelatedStories(story).then(relatedStories => {
      this._isMounted &&
        this.setState({
          relatedStories: relatedStories
        });
    });
  }

  componentDidMount() {
    this._isMounted = true;
    !this.state.relatedStories.length && this.updateRelatedStories(this.props.story);
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  getRelatedStoryTemplate() {
    const isReviewStory = get(this.props, ["story", "story-template"], "") === "review";
    if (isReviewStory) {
      return this.getVerticalRelatedStories();
    }
    const { template } = this.props;

    switch (template) {
      case "hero-priority":
        return this.getHorizontalRelatedStories();
      case "hero-overlay":
        return this.getHorizontalRelatedStories();
      case "headline-hero-priority":
        return this.getHorizontalRelatedStories();
      case "headline-sideway":
        return this.getHorizontalRelatedStories();
      default:
        return this.getVerticalRelatedStories();
    }
  }

  getVerticalRelatedStories() {
    const { relatedStories = [] } = this.state;
    const [firstStory, ...remainingStories] = relatedStories;
    const wrapperStyles = this.props.wide ? "wrapper wide" : "wrapper";
    return (
      <div styleName={wrapperStyles} data-testid="related-stories">
        <h4 styleName="heading"> Related Stories </h4>
        <RelatedStoryCard story={firstStory} showImage={true} />
        {(remainingStories || []).map(story => (
          <RelatedStoryCard key={story.id} story={story} showImage={false} />
        ))}
      </div>
    );
  }

  getHorizontalRelatedStories() {
    return (
      <div styleName="container">
        <h4 styleName="heading"> Related Stories </h4>
        <div styleName="wrapper" data-testid="related-stories">
          {this.state.relatedStories.map(story => (
            <RelatedStoryCard key={story.id} story={story} showImage={true} />
          ))}
        </div>
      </div>
    );
  }

  render() {
    return !this.state.relatedStories || !this.state.relatedStories.length ? null : this.getRelatedStoryTemplate();
  }
}
RelatedStories.propTypes = {
  story: PropTypes.object,
  wide: PropTypes.bool,
  template: PropTypes.string,
  staticRelatedStories: PropTypes.array
};

export { RelatedStories };
